import React, { useEffect, useMemo } from 'react'
import FileItem from '../FileItem/index'

import { Folder, File, ObjectType } from '@/modules/Drive/types'
import useDriveStore from '@/stores/useDriveStore'
import { useFilePreview } from '@/contexts/file-preview'
import { GetEmployeeNamesByUuid } from '@/modules/Drive/services'
import { t } from 'i18next'
import {
    extractExtension,
    extractName,
    extractUuidFromObjectKey,
} from '../../utils'

interface FileGridProps {
    folders?: Folder[]
    files?: File[]
}

const style = {
    grid: 'grid gap-4 lg:grid-cols-5 md:grid-cols-3 grid-cols-1',
    list: 'flex flex-col gap-2 ',
}

const FileGrid: React.FC<FileGridProps> = ({ folders = [], files = [] }) => {
    const downloadFile = useDriveStore((state) => state.downloadFile)
    const viewType = useDriveStore((state) => state.viewType)
    const itemNames = useDriveStore((state) => state.itemNames)
    const appendItemName = useDriveStore((state) => state.appendItemName)
    const { previewMultiple } = useFilePreview()

    const allFiles = useMemo(
        () =>
            files.map((file) => ({
                name: file.Key,
                extension: extractExtension(file.Key),
                size: file.Size,
                lastModified: file.LastModified,
                type: ObjectType.FILE,
            })),
        [files]
    )

    const handleMultiplePreview = (startIndex: number) => {
        const fileItems = allFiles.map((file) => {
            const isHeic = file.extension.toLowerCase() === 'heic'
            return {
                fileKey: file.name,
                attachmentInfo: {
                    attachment_file_name: extractName(file.name),
                    attachment_file_type: isHeic ? 'image/jpeg' : undefined,
                },
                manualType: isHeic,
                extension: file.extension,
                isHeic, // Add flag to indicate HEIC file for special handling
            }
        })
        previewMultiple(fileItems, startIndex)
    }

    const fetchItemNames = async () => {
        folders.forEach((folder) => {
            if (folder.FolderName) {
                appendItemName(
                    extractUuidFromObjectKey(folder.Prefix),
                    folder.FolderName
                )
            }
        })

        const missing = folders.filter(
            (f) =>
                !f.FolderName &&
                !itemNames[f.Prefix] &&
                f.Prefix.split('/').filter(Boolean).length === 3
        )

        const uuids = new Set(
            missing.map((f) => extractUuidFromObjectKey(f.Prefix))
        )
        if (uuids.size === 0) return

        // get employee names by uuids
        const results = await GetEmployeeNamesByUuid(Array.from(uuids))

        if (results.result) {
            results.result.forEach((result) => {
                appendItemName(result.employee_uuid, result.employee_name)
            })
        }
    }

    useEffect(() => {
        fetchItemNames()
    }, [folders])

    const isSelectedMode = useDriveStore((state) => state.isSelectedMode)

    return (
        <div>
            {/* Header row for list view */}
            {viewType === 'list' && (
                // move this component 10px to top
                <div className="sticky top-[-16px] bg-white z-10 border-b border-gray-200">
                    <div className="flex flex-row items-center gap-4 py-2 px-3 text-sm font-semibold text-gray-600">
                        {isSelectedMode && (
                            <div className="size-5 shrink-0"></div>
                        )}
                        <div className="size-8 shrink-0"></div>
                        <div className="flex-1 min-w-0">
                            {t('drive.column.name')}
                        </div>
                        <div className="hidden sm:block w-20 flex-shrink-0 text-right">
                            {t('drive.column.size')}
                        </div>
                        <div className="hidden sm:block w-40 flex-shrink-0 text-right">
                            {t('drive.column.last-modified')}
                        </div>
                        <div className="w-16 flex-shrink-0 text-right">
                            {t('drive.column.action')}
                        </div>
                    </div>
                </div>
            )}
            <div className={style[viewType]}>
                {folders?.map((folder) => (
                    <FileItem
                        key={folder.Prefix}
                        objectKey={folder.Prefix}
                        type={ObjectType.FOLDER}
                        folderName={folder.FolderName}
                        downloadHandler={() =>
                            downloadFile({
                                type: ObjectType.FOLDER,
                                key: folder.Prefix,
                                itemName: extractName(folder.Prefix),
                            })
                        }
                    />
                ))}
                {files?.map((file, index) => (
                    <FileItem
                        key={file.Key}
                        objectKey={file.Key}
                        type={ObjectType.FILE}
                        size={file.Size}
                        lastModified={file.LastModified}
                        downloadHandler={() =>
                            downloadFile({
                                type: ObjectType.FILE,
                                key: file.Key,
                                itemName: extractName(file.Key),
                            })
                        }
                        onMultiplePreview={() => handleMultiplePreview(index)}
                        totalFiles={allFiles.length}
                    />
                ))}
            </div>
        </div>
    )
}

export default FileGrid
